
import Vue from 'vue';
import { Data as ArticleData, Headlines, ContentFragment as ArticleContentFragment } from '@root/modules/article/types/article';
import ContentRecipe from '@core/components/UI/ContentRecipe.vue';
import ContentFragment from '@core/components/ContentFragment.vue';
import AdFormBanner from '@ads/components/adForm/AdFormBanner.vue';
import ArticleBodyBottom from '@root/modules/article/components/ArticleBodyBottom.vue';
import ArticleHeadlinesSection from '@root/modules/article/components/headlines/ArticleHeadlinesSection.vue';
import BodySkeletonLoader from '@root/modules/article/components/BodySkeletonLoader.vue';
import PianoCustomerStateHandler from '@piano/components/PianoCustomerStateHandler.vue';

const ArticlePaywall = () => import(/* webpackChunkName: "ArticlePaywall" */ '@root/modules/article/components/paywall/ArticlePaywall.vue');
const ArticleRegwall = () => import(/* webpackChunkName: "ArticleRegwall" */ '@root/modules/article/components/regwall/ArticleRegwall.vue');
const ArticleNewsletter = () => import(/* webpackChunkName: "ArticleNewsletter" */ '@root/modules/article/components/newsletter/ArticleNewsletter.vue');

import getBannerKey from '@root/modules/ads/utils/getBannerKey';
import getHelplineFile from '@root/modules/article/utils/getHelplineFile';
import TellUs from '@root/modules/article/components/settings/TellUs.vue';
import { isMobileOrTablet } from '@ads/utils/adx';

interface TikWindow extends Window {
  tik4: any;
}

interface TickarooLiveblogElement extends HTMLElement {
  adsAttached?: boolean;
  setAdManager: (ad: any) => void;
}

declare const window: TikWindow;

interface Props {
  article: ArticleData;
  headlines: Headlines | [];
  isArticleFetching: boolean;
  fontSizeClass: string;
}

interface Computed {
  articleGroupedBody: ArticleData['content']['groupedBody'];
  disabledAdsList: string[];
  disableAds: boolean;
  adPlacements: Record<string, any>;
  bodyFirstAd: Record<string, any>;
  isCrArticle: boolean;
  crIndex: number | null;
  helpline: Record<string, any>[];
  isLiveBlogArticle: boolean;
  isLoggedIn: boolean;
  isAdFree: boolean;
}

interface Data {
  initFragmentsGroup: FragmentsGroup;
  relatedArticlesSettings: {
    minParagraphsLength: number;
    afterParagraphPosition: number;
    articlesLimit: number;
    articlesInBodyLimit: number;
    endOfArticleBody: boolean;
    disableRelatedArticles: boolean;
  };
  liveBlogAdStep: number;
  liveBlogAdStart: number;
}

interface Methods {
  getPlacement: (index: number, type: string) => string | null;
  getKey: (params: Record<string, any>) => string;
  getHelplineFile: typeof getHelplineFile;
  createLiveBlogAdSlot: (index: number) => string;
  initTickarooLiveblogAds: () => void;
}

type Fragment = ArticleContentFragment;

interface FragmentsGroup {
  items: (Fragment | string)[];
  options: {
    skipAds: boolean;
    addRelatedHeadlines: boolean;
    relatedPosition: number;
  };
}

export default Vue.extend<Data, Methods, Computed, Props>({
  components: {
    TellUs,
    AdFormBanner,
    ArticleNewsletter,
    ArticleBodyBottom,
    ContentFragment,
    ContentRecipe,
    ArticleHeadlinesSection,
    ArticlePaywall,
    ArticleRegwall,
    BodySkeletonLoader,
    PianoCustomerStateHandler,
  },
  props: {
    article: {
      type: Object,
      required: true,
    },
    headlines: {
      type: Array,
      required: true,
    },
    isArticleFetching: {
      type: Boolean,
      required: false,
      default: false,
    },
    fontSizeClass: {
      type: String,
      required: false,
      default: 'article__body-fs-1',
    },
  },
  data() {
    return {
      initFragmentsGroup: {
        items: [],
        options: {
          skipAds: false,
          addRelatedHeadlines: false,
          relatedPosition: 1,
        },
      },
      relatedArticlesSettings: this.$channelConfig('page').article.component.relatedArticles,
      liveBlogAdStep: 2,
      liveBlogAdStart: 2,
    };
  },
  computed: {
    disabledAdsList() {
      return this.$store.getters['adForm/getDisabledAdsList'];
    },
    disableAds() {
      return this.article.banners.disableAds;
    },
    articleGroupedBody() {
      return this.article.content.groupedBody;
    },
    adPlacements() {
      const placements: Record<string, any> = this.$store.getters['adForm/getAdsPlacements'];

      return placements || {};
    },
    bodyFirstAd() {
      const placements: Record<string, any> = { desktop: null, mobile: null, adb: null };

      if (this.adPlacements?.bodyfirst?.mid && this.adPlacements?.bodyfirst?.mid?.length && this.adPlacements?.bodyfirst?.mid[0]) {
        placements.desktop = this.adPlacements.bodyfirst.mid[0];
      }

      if (this.adPlacements?.mobilebodyfirst?.mid && this.adPlacements?.mobilebodyfirst?.mid?.length && this.adPlacements?.mobilebodyfirst?.mid[0]) {
        placements.mobile = this.adPlacements.mobilebodyfirst.mid[0];
      }

      if (this.adPlacements?.mobilebodyfirst && this.adPlacements?.mobilebodyfirst?.adb) {
        placements.adb = this.adPlacements.mobilebodyfirst.adb;
      }

      return placements;
    },
    isCrArticle() {
      const articles = this.$store.getters['article/getCrArticles'];

      return articles.find((article: number) => article === this.article.id);
    },
    crIndex() {
      if (this.isCrArticle) {
        const id = this.article.id;
        const crArticles = this.$store.state.article.crArticles;
        return crArticles.indexOf(id) + 1;
      }

      return null;
    },
    helpline() {
      const helpline = this.article.helpline.filter((item) => item.value === true);

      return helpline;
    },
    isLiveBlogArticle() {
      return this.article.settings?.articleTemplate && this.article.settings?.articleTemplate === 'liveBlog';
    },
    isLoggedIn() {
      return this.$store.state.piano.isLoggedIn;
    },
    isAdFree() {
      return this.$store.state.piano.access.adFree || false;
    },
  },
  methods: {
    getPlacement(index, type) {
      const placements = this.adPlacements[type]?.mid || [];
      const placement = placements[index] || null;

      return placement;
    },
    getKey({ prefix, index }) {
      return getBannerKey({ prefix, route: this.$route, store: this.$store, index });
    },
    getHelplineFile,
    createLiveBlogAdSlot(index: number) {
      const placements = isMobileOrTablet() ? this.adPlacements?.mobile?.mid : this.adPlacements?.square?.mid;
      const placement = placements[(index - this.liveBlogAdStart) / this.liveBlogAdStep];
      if (!placement) {
        return '';
      }

      const randomString = Math.random()
        .toString(36)
        .slice(2, 2 + 7);

      if (placement.startsWith('DIM:')) {
        return `<div id="${placement.slice(4)}"></div><sc` + `ript>window.ibbTag('${placement.slice(4)}', {mark: 1})</scr` + `ipt>`;
      } else {
        return (
          `<div id="${placement}--${randomString}"></div><sc` + `ript>window.adxTag(${placement}, {mark: 1, dom:"${placement}--${randomString}"})</scr` + `ipt>`
        );
      }
    },
    initTickarooLiveblogAds() {
      if (this.disableAds || this.isAdFree) {
        return;
      }

      const liveBlog = document.querySelector('tickaroo-liveblog') as TickarooLiveblogElement;
      if (!liveBlog) {
        return;
      }

      const attachAds = () => {
        if (liveBlog.adsAttached || !liveBlog.setAdManager || !window.tik4) {
          return;
        }

        liveBlog.adsAttached = true;
        liveBlog.setAdManager(
          new window.tik4.ComplexAdManager('AboveEvent', [
            {
              every: this.liveBlogAdStep,
              first: this.liveBlogAdStart,
              adCode: this.createLiveBlogAdSlot,
            },
          ])
        );
      };

      liveBlog.addEventListener('tickarooLiveblogInit', attachAds);
      attachAds();
    },
  },
  async mounted() {
    this.$nextTick(() => {
      if (!this.isLiveBlogArticle) {
        return;
      }

      if (this.$store.state.piano.isScriptInited) {
        this.initTickarooLiveblogAds();
      } else {
        this.$store.watch(
          (state) => state.piano.isScriptInited,
          async () => {
            this.initTickarooLiveblogAds();
          }
        );
      }
    });
  },
});
